/**
 * Validade de documento.
 *
 * PARIDADE COM O LEGADO (docs/legacy-spec/ged.md §9)
 * ---------------------------------------------------
 *   - `validityMonths = 0` → documento SEM validade. Sentinela usada em todas
 *     as queries do legado.                                            → PRESERVADO
 *   - Fórmula: `validUntil = revisionDate + validityMonths` (meses de
 *     calendário).                                                     → PRESERVADO
 *   - Só documentos APROVADOS vencem (`model/ged.php:90-96`).           → PRESERVADO
 *   - Semáforo da Lista Mestra: vermelho < 0 dias, amarelo 0..15,
 *     verde > 15, cinza sem validade (`ged_list.twig:220-228`).         → PRESERVADO
 *   - Janela "vencendo" do dashboard: 30 dias (`model/ged.php:996`).    → PRESERVADO
 *
 * DIVERGÊNCIAS DELIBERADAS — docs/divergences.md
 *   D-VAL-01  O legado calculava `validade_date` 100% no CLIENTE (JS) e gravava
 *             o texto como veio, sem recalcular nem validar coerência. Aqui o
 *             cálculo é do servidor; o cliente só pré-visualiza.
 *   D-VAL-02  O legado usava `setMonth()` do JS, que faz overflow de dia:
 *             31/01 + 1 mês = 03/03. Aqui saturamos no último dia do mês:
 *             31/01 + 1 mês = 28/02. Ver `addCalendarMonths`.
 *   D-VAL-03  O legado tinha um piso de 1 mês no JS (`if (meses < 1) meses = 1`)
 *             que contradizia a sentinela 0 = sem validade. Removido: 0 significa
 *             sem validade, e é rejeitado se o documento declarar ter validade.
 *   D-VAL-04  O legado avaliava vencimento com 3 predicados SQL divergentes em
 *             5 lugares (ged.md §9.3). Aqui há UMA função e o estado é
 *             MATERIALIZADO por job noturno (correção C8 do plano).
 *   D-VAL-05  No legado o vencimento era puramente informativo: não bloqueava
 *             nada e não notificava ninguém (BUG-83). O que mudou aqui:
 *               · o DOWNLOAD do binário continua liberado para versão vencida —
 *                 paridade literal com o legado (ged.md §9.5) —, mas passou a
 *                 ser sempre AUDITADO;
 *               · a IMPRESSÃO, essa sim, exige `document:read:all` quando a
 *                 versão não é vigente, porque a cópia impressa sai do sistema
 *                 e circula sem controle de versão (`resolveFileRelease` em
 *                 state-machine.ts, ISO 13485 §4.2.4(d));
 *               · existe job de notificação respeitando `freq_aviso` do tipo
 *                 (BUG-11 do legado, campo gravado e nunca lido).
 */
import { ValidityState } from '@qualicloud/contracts';
/** Janela amarela da Lista Mestra, em dias. `ged_list.twig:222` */
export const EXPIRING_SOON_LIST_DAYS = 15;
/** Janela do dashboard, em dias. `model/ged.php:996` */
export const EXPIRING_SOON_DASHBOARD_DAYS = 30;
/** Frequência de aviso de vencimento, por tipo de documento. Legado: `freq_aviso`. */
export const NoticeFrequency = {
    NONE: 'NONE',
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
};
/** `D`/`S`/`M` do legado → enum. Usado pelo ETL. */
export const LEGACY_NOTICE_FREQUENCY = {
    '': NoticeFrequency.NONE,
    D: NoticeFrequency.DAILY,
    S: NoticeFrequency.WEEKLY,
    M: NoticeFrequency.MONTHLY,
};
/**
 * Soma meses de calendário SATURANDO no último dia do mês (D-VAL-02).
 * 31/01 + 1 mês → 28/02 (ou 29/02 em ano bissexto), não 03/03.
 *
 * Opera em UTC para não sofrer com horário de verão.
 */
export function addCalendarMonths(base, months) {
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth();
    const day = base.getUTCDate();
    const targetMonthIndex = month + months;
    const targetYear = year + Math.floor(targetMonthIndex / 12);
    const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
    // Dia 0 do mês seguinte = último dia do mês alvo.
    const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const safeDay = Math.min(day, lastDayOfTargetMonth);
    return new Date(Date.UTC(targetYear, targetMonth, safeDay));
}
export class InvalidValidityError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'InvalidValidityError';
    }
}
/**
 * Calcula a data de vencimento no SERVIDOR (D-VAL-01).
 * Retorna `null` quando o documento não tem validade.
 */
export function computeValidUntil(params) {
    const { revisionDate, validityMonths } = params;
    if (!Number.isInteger(validityMonths) || validityMonths < 0) {
        throw new InvalidValidityError('A validade em meses precisa ser um número inteiro maior ou igual a zero.');
    }
    // Sentinela do legado preservada.
    if (validityMonths === 0)
        return null;
    return addCalendarMonths(revisionDate, validityMonths);
}
/**
 * Valida a coerência do trio (temValidade, meses, dataCalculada).
 * O legado só validava `val_month >= 1` no servidor (`ged.php:296-302`)
 * e a validação equivalente no cliente estava quebrada por seletor errado (BUG-20).
 */
export function assertValidityIsCoherent(params) {
    if (params.hasValidity && params.validityMonths < 1) {
        throw new InvalidValidityError('Informe a quantidade de meses para validade do documento.');
    }
    if (!params.hasValidity && params.validityMonths !== 0) {
        throw new InvalidValidityError('Documento sem validade precisa ter a quantidade de meses igual a zero.');
    }
}
/** Dias restantes até o vencimento. Negativo = vencido há N dias. Legado: `cont_vencimento`. */
export function daysUntilExpiry(validUntil, now) {
    if (!validUntil)
        return null;
    const MS_PER_DAY = 86_400_000;
    const a = Date.UTC(validUntil.getUTCFullYear(), validUntil.getUTCMonth(), validUntil.getUTCDate());
    const b = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return Math.round((a - b) / MS_PER_DAY);
}
/**
 * Estado de validade — predicado ÚNICO e canônico (D-VAL-04).
 *
 * @param warningWindowDays 15 para a Lista Mestra, 30 para o dashboard.
 */
export function resolveValidityState(params) {
    const { validUntil, validityMonths, now } = params;
    const window = params.warningWindowDays ?? EXPIRING_SOON_LIST_DAYS;
    if (validityMonths === 0 || !validUntil)
        return ValidityState.NOT_APPLICABLE;
    const days = daysUntilExpiry(validUntil, now);
    if (days === null)
        return ValidityState.NOT_APPLICABLE;
    if (days < 0)
        return ValidityState.EXPIRED;
    if (days <= window)
        return ValidityState.EXPIRING_SOON;
    return ValidityState.VALID;
}
/**
 * Um documento vence apenas se estiver APROVADO — regra do legado
 * (`model/ged.php:90-96`) preservada. Usado pelo job noturno que MATERIALIZA
 * a transição APPROVED → EXPIRED.
 */
export function shouldTransitionToExpired(params) {
    if (params.status !== 'APPROVED')
        return false;
    return (resolveValidityState({
        validUntil: params.validUntil,
        validityMonths: params.validityMonths,
        now: params.now,
    }) === ValidityState.EXPIRED);
}
export function validitySemaphore(state) {
    switch (state) {
        case ValidityState.EXPIRED:
            return 'red';
        case ValidityState.EXPIRING_SOON:
            return 'amber';
        case ValidityState.VALID:
            return 'green';
        case ValidityState.NOT_APPLICABLE:
        default:
            return 'grey';
    }
}
/** Próxima data de aviso, conforme a frequência configurada no tipo (corrige BUG-11). */
export function shouldNotifyToday(params) {
    const { frequency, lastNotifiedAt, now } = params;
    if (frequency === NoticeFrequency.NONE)
        return false;
    if (!lastNotifiedAt)
        return true;
    const days = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) -
        Date.UTC(lastNotifiedAt.getUTCFullYear(), lastNotifiedAt.getUTCMonth(), lastNotifiedAt.getUTCDate())) /
        86_400_000);
    switch (frequency) {
        case NoticeFrequency.DAILY:
            return days >= 1;
        case NoticeFrequency.WEEKLY:
            return days >= 7;
        case NoticeFrequency.MONTHLY:
            return days >= 30;
        default:
            return false;
    }
}
//# sourceMappingURL=validity.js.map